import dedent from 'ts-dedent'
import {
  GrantsAndAllowedApis,
  Provider,
  QueryAndOutputs,
  TokenAllowedApis,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentGetRequiredGrantsParams,
  WorkflowComponentOutputTablesParams,
  WorkflowComponentParamType,
  WorkflowComponentValue
} from '../types'
import { isExpressionValue } from '../utils'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class ImportURL extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.importurl',
        group: 'Input/Output',
        title: 'Import from URL',
        description: `
      This components imports a table from a URL.
      `,
        starred: true,
        warningTitle: `Run required`,
        warningDescription: `It's required to run the workflow before connecting it to any other element. This process may take several minutes.`
      },
      [
        {
          name: 'url',
          type: WorkflowComponentParamType.String,
          title: 'Source URL',
          placeholder: 'E.g.: https://storage.googleapis.com/carto_test_tables/places.zip'
        },
        {
          name: 'autoguessing',
          type: WorkflowComponentParamType.Boolean,
          title: 'Automatically guess column data types in the imported table',
          default: true
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    let query
    const dryRun = codeType === WorkflowCodeType.DryRun
    const tablenameFull = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      WorkflowCodeType.Full
    )
    const tablenameDryRun = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      WorkflowCodeType.DryRun
    )
    if (dryRun) {
      query = connection.getCreateTempTableQuery(
        tablenameDryRun,
        `SELECT * FROM ${connection.quoteName(tablenameFull)}`,
        codeType,
        null
      )
    } else {
      query = this.getInnerSql({
        params,
        connection,
        envVars,
        tablename: tablenameFull,
        workflow,
        componentName
      } as WorkflowComponentCodeParams)
    }
    const outputs: Record<string, string> = {}
    outputs[SINGLE_OUTPUT_NAME] = dryRun ? tablenameDryRun : tablenameFull
    return {
      outputs: outputs,
      query: query
    }
  }

  getInnerSql({ params, connection, envVars, tablename, componentName, workflow }: WorkflowComponentCodeParams) {
    const preparedParams = this._prepareParameters(params)

    const originalUrl = (workflow.componentValues[componentName].url as WorkflowComponentValue).value as string

    let url
    if (isExpressionValue(originalUrl)) {
      url = preparedParams.url
    } else {
      url = `'${preparedParams.url}'`
    }

    if (connection.type === Provider.BigQuery) {
      return dedent`
          CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.IMPORT_URL(
            '${envVars.apiBaseUrl}',
            '${envVars.accessToken}',
            ${url},
            '${connection.name}',
            '${tablename}',
            '{"autoguessing": ${preparedParams.autoguessing}}'
          );`
    } else if (connection.type === Provider.Snowflake) {
      return dedent`
          CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.IMPORT_URL(
            ${url},
            '${connection.name}',
            '${tablename}',
            '{"autoguessing": ${preparedParams.autoguessing}}',
            '${envVars.apiBaseUrl}',
            '${envVars.accessToken}'
          );`
    } else if (connection.type === Provider.Redshift) {
      return dedent`
          DECLARE
            url VARCHAR(MAX) DEFAULT ${url};
          BEGIN
            CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.IMPORT_URL(
              url,
              '${connection.name}',
              '${tablename}',
              '{"autoguessing": ${preparedParams.autoguessing}}',
              '${envVars.apiBaseUrl}',
              '${envVars.accessToken}'
            );
          END;`
    } else {
      return ''
    }
  }

  getRequiredGrants({
    workflow,
    connection,
    params,
    componentName
  }: WorkflowComponentGetRequiredGrantsParams): GrantsAndAllowedApis {
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      WorkflowCodeType.Full
    )
    return {
      grants: [
        {
          connection_name: connection.name,
          source: tablename
        }
      ],
      apis: [TokenAllowedApis.imports]
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.BigQuery:
        return '1.14.0'
      case Provider.Snowflake:
        return '1.12.0'
      case Provider.Redshift:
        return '1.7.0'
      default:
        return null
    }
  }

  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Redshift]
  }

  getOutputTables({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentOutputTablesParams): Record<string, string> {
    if (codeType === WorkflowCodeType.StoredProcedure) {
      codeType = WorkflowCodeType.Full
    }
    return {
      [SINGLE_OUTPUT_NAME]: connection.getOutputTableName(
        workflow.id,
        componentName,
        params,
        SINGLE_OUTPUT_NAME,
        codeType
      )
    }
  }
}

export { ImportURL }
